import React, { useEffect, useState } from "react";
import { formatINR } from "@/lib/api";

// Two-tier fabric picker with a colour filter:
//   0. "Filter by Colour" — chips built from the colours the admin set on
//      each fabric (Admin -> Fabrics). "All" clears the filter.
//   1. "Choose Fabric Type" — radio list of distinct fabric types.
//   2. The fabric photos for that type, each showing its colour + rate.
const swatch = (c) => String(c || "").trim().toLowerCase().replace(/\s+/g, "");

export default function FabricPicker({ fabrics, selected, onSelect, testidPrefix = "fabric" }) {
  const [activeType, setActiveType] = useState(null);
  const [colorFilter, setColorFilter] = useState(null);

  const colors = React.useMemo(() => {
    const s = new Set();
    (fabrics || []).forEach((f) => (f.colors || []).forEach((c) => c && s.add(c)));
    return Array.from(s);
  }, [fabrics]);

  const colorMatch = (f) => !colorFilter || (f.colors || []).includes(colorFilter);

  const types = React.useMemo(() => {
    const map = new Map();
    for (const f of fabrics || []) {
      if (colorFilter && !(f.colors || []).includes(colorFilter)) continue;
      const t = f.fabric_type || f.sub_category || "Fabric";
      if (!map.has(t)) map.set(t, { type: t, minPrice: f.price, count: 0 });
      const entry = map.get(t);
      entry.minPrice = Math.min(entry.minPrice, f.price);
      entry.count += 1;
    }
    return Array.from(map.values());
  }, [fabrics, colorFilter]);

  useEffect(() => {
    if (types.length > 0 && !types.find((t) => t.type === activeType)) setActiveType(types[0].type);
  }, [types, activeType]);

  if (!fabrics || fabrics.length === 0) {
    return <p className="text-sm text-[#4B5563]">No fabrics available yet — add some from the Admin → Fabrics panel.</p>;
  }

  const shown = fabrics.filter((f) => (f.fabric_type || f.sub_category || "Fabric") === activeType && colorMatch(f));

  return (
    <div>
      {colors.length > 0 && (
        <div className="mb-8">
          <div className="text-sm font-medium mb-3">Filter by Colour</div>
          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid={`${testidPrefix}-color-all`} onClick={() => setColorFilter(null)}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.12em] border ${!colorFilter ? "border-black bg-black text-white" : "border-black/20 hover:border-black"}`}>All</button>
            {colors.map((c) => (
              <button key={c} type="button" data-testid={`${testidPrefix}-color-${c}`} onClick={() => setColorFilter(c === colorFilter ? null : c)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs border ${colorFilter === c ? "border-black bg-black text-white" : "border-black/20 hover:border-black"}`}>
                <span className="w-3 h-3 rounded-full border border-black/20" style={{ background: swatch(c) }} />
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="text-sm font-medium mb-3">Choose Fabric Type *</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4 mb-10">
        {types.map((t) => (
          <button key={t.type} type="button" data-testid={`${testidPrefix}-type-${t.type}`} onClick={() => setActiveType(t.type)} className="flex items-center gap-3 text-left">
            <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${activeType === t.type ? "border-black" : "border-black/30"}`}>
              {activeType === t.type && <span className="w-2 h-2 rounded-full bg-black" />}
            </span>
            <span className="text-sm">{t.type}</span>
          </button>
        ))}
      </div>

      {activeType && (
        <div>
          <div className="text-sm font-medium mb-3">{activeType} Fabric *</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {shown.map((f) => (
              <button key={f.id} type="button" data-testid={`${testidPrefix}-${f.id}`} onClick={() => onSelect(f)}
                className={`text-left border ${selected?.id === f.id ? "border-black" : "border-transparent hover:border-black/15"}`}>
                <div className="aspect-square overflow-hidden bg-[#F5F5F5] flex items-center justify-center">
                  <img src={f.images?.[0]} alt={f.name} className="w-full h-full object-contain" />
                </div>
                <div className="p-2">
                  <div className="text-xs font-medium truncate">{f.name}</div>
                  <div className="text-[11px] text-[#4B5563] mt-0.5">{formatINR(f.price)}</div>
                  {f.colors?.length > 0 && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      {f.colors.slice(0, 4).map((c) => (
                        <span key={c} title={c} className="w-3 h-3 rounded-full border border-black/20" style={{ background: swatch(c) }} />
                      ))}
                      <span className="text-[10px] text-[#4B5563] truncate">{f.colors.join(", ")}</span>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
